import { PERMISSIONS } from '@ibe/constants';
import { Button, ErrorState, Field, FieldLabel, FieldSelect, LoadingState } from '@ibe/ui';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { usePaymentGateways } from '../../payment-gateways/index.js';
import { useUpdatePropertySetup } from '../api/mutations.js';
import { usePropertySetup, usePropertySetupGateways, useSetupProperties } from '../api/queries.js';
import { propertySetupFormInput, type PropertySetupFormInput } from '../api/schemas.js';
import { PropertySetupFields } from '../forms/property-setup-fields.js';

const DEFAULT_FORM_VALUES: PropertySetupFormInput = {
  propertyId: '',
  facebookUrl: '',
  instagramUrl: '',
  twitterUrl: '',
  totalRooms: '0',
  smtpGatewayId: '',
  smsGatewayId: '',
  paymentGatewayId: '',
  selectedAmenities: {},
  amenityFree: {},
};

export function PropertySetupPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [propertyId, setPropertyId] = useState<string>(searchParams.get('propertyId') ?? '');

  const properties = useSetupProperties();
  const setup = usePropertySetup(propertyId);
  const gateways = usePropertySetupGateways(propertyId);
  const paymentGateways = usePaymentGateways();
  const update = useUpdatePropertySetup();

  const form = useAppForm<PropertySetupFormInput>({
    schema: propertySetupFormInput,
    defaultValues: DEFAULT_FORM_VALUES,
    successMessage: 'Property setup saved.',
    onSubmit: async (values) => {
      await update.mutateAsync({ propertyId: values.propertyId, values });
    },
  });

  const { reset } = form;

  useEffect(() => {
    if (setup.data?.propertyId !== propertyId) return;

    const selectedAmenities: Record<string, boolean> = {};
    const amenityFree: Record<string, boolean> = {};
    for (const amenity of setup.data.amenities) {
      selectedAmenities[amenity.amenityId] = amenity.isSelected;
      amenityFree[amenity.amenityId] = amenity.isFree;
    }

    reset({
      propertyId: setup.data.propertyId,
      facebookUrl: setup.data.configuration.facebookUrl ?? '',
      instagramUrl: setup.data.configuration.instagramUrl ?? '',
      twitterUrl: setup.data.configuration.twitterUrl ?? '',
      totalRooms: String(setup.data.configuration.totalRooms),
      smtpGatewayId: setup.data.configuration.smtpGatewayId ?? '',
      smsGatewayId: setup.data.configuration.smsGatewayId ?? '',
      paymentGatewayId: setup.data.configuration.paymentGatewayId ?? '',
      selectedAmenities,
      amenityFree,
    });
  }, [setup.data, propertyId, reset]);

  const propertyOptions = (properties.data?.items ?? []).map((p) => ({
    value: p.id,
    label: `${p.name} (${p.code})`,
  }));

  const emailGatewayOptions = (gateways.data?.email ?? []).map((g) => ({
    value: g.id,
    label: g.name,
  }));

  const smsGatewayOptions = (gateways.data?.sms ?? []).map((g) => ({
    value: g.id,
    label: g.name,
  }));

  const paymentGatewayOptions = (paymentGateways.data?.items ?? [])
    .filter((g) => g.isActive === true)
    .map((g) => ({
      value: g.id,
      label: g.name ?? '',
    }));

  return (
    <PermissionGuard permission={PERMISSIONS.PROPERTY_SETUP} fallback={<NotPermitted />}>
      <section className="space-y-6">
        <header className="rounded-xl border border-border bg-card p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">
            Property · Setup
          </p>
          <h1 className="mt-2 text-2xl font-semibold">Property setup</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure social links, notification gateways and amenities for a property.
          </p>
        </header>

        <section className="space-y-4 rounded-xl border border-border bg-card p-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Section 01
            </p>
            <h2 className="text-lg font-semibold">Select property</h2>
          </div>

          <Field>
            <FieldLabel>Property</FieldLabel>
            <FieldSelect
              value={propertyId}
              onValueChange={(next) => {
                setPropertyId(next);
                form.setValue('propertyId', next);
                const updated = new URLSearchParams(searchParams);
                if (next === '') {
                  updated.delete('propertyId');
                } else {
                  updated.set('propertyId', next);
                }
                setSearchParams(updated, { replace: true });
              }}
              options={propertyOptions}
              placeholder="Select a property"
            />
          </Field>
        </section>

        {propertyId !== '' ? (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void form.submit();
            }}
            className="space-y-6"
          >
            {setup.isPending || gateways.isPending || paymentGateways.isPending ? (
              <LoadingState label="Loading property setup" />
            ) : setup.error !== null ? (
              <ErrorState
                description={errorTextFor(setup.error) ?? ''}
                onRetry={() => {
                  void setup.refetch();
                }}
              />
            ) : (
              <>
                <PropertySetupFields
                  key={propertyId}
                  form={form}
                  emailGatewayOptions={emailGatewayOptions}
                  smsGatewayOptions={smsGatewayOptions}
                  paymentGatewayOptions={paymentGatewayOptions}
                  amenities={setup.data.amenities}
                />

                <div className="flex justify-end gap-3">
                  <Button type="submit" disabled={form.submitting || update.isPending}>
                    {form.submitting || update.isPending ? 'Saving…' : 'Save setup'}
                  </Button>
                </div>
              </>
            )}
          </form>
        ) : null}
      </section>
    </PermissionGuard>
  );
}
